// src/hooks/useDesktopDropdown.ts
import { useEffect } from "react";

type Opts = {
  itemSelector?: string;        // li that has children
  labelSelector?: string;       // clickable/hoverable label (a/button)
  submenuSelector?: string;     // the UL submenu
  openClass?: string;           // class to mark open
  desktopMinWidth?: number;     // px breakpoint where desktop behavior starts
  closeOnEsc?: boolean;
};

export function useDesktopDropdown(opts: Opts = {}) {
  const {
    itemSelector = ".navbar__item--has-children",
    labelSelector = ".navbar__dropdown-label",
    submenuSelector = ".navbar__sub-menu",
    openClass = "is-open",
    desktopMinWidth = 1200, // matches your d-xl-* usage
    closeOnEsc = true,
  } = opts;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mql = window.matchMedia(`(min-width: ${desktopMinWidth}px)`);
    const root = document; // limit to main navbar if you like: document.querySelector(".primary-navbar") ?? document;

    const items = Array.from(root.querySelectorAll<HTMLElement>(itemSelector));

    // helper: close all
    const closeAll = () => {
      items.forEach((li) => {
        li.classList.remove(openClass);
        const label = li.querySelector<HTMLElement>(labelSelector);
        if (label) label.setAttribute("aria-expanded", "false");
        const sub = li.querySelector<HTMLElement>(submenuSelector);
        if (sub) (sub.style.display = "none");
      });
    };

    // open a specific li
    const openItem = (li: HTMLElement) => {
      items.forEach((other) => {
        if (other !== li) other.classList.remove(openClass);
        const otherLabel = other.querySelector<HTMLElement>(labelSelector);
        if (otherLabel) otherLabel.setAttribute("aria-expanded", "false");
        const otherSub = other.querySelector<HTMLElement>(submenuSelector);
        if (otherSub) otherSub.style.display = "none";
      });
      li.classList.add(openClass);
      const label = li.querySelector<HTMLElement>(labelSelector);
      if (label) label.setAttribute("aria-expanded", "true");
      const sub = li.querySelector<HTMLElement>(submenuSelector);
      if (sub) sub.style.display = "block";
    };

    // desktop listeners
    const onMouseEnter = (e: MouseEvent) => {
      if (!mql.matches) return; // only desktop
      const li = (e.currentTarget as HTMLElement);
      openItem(li);
    };
    const onMouseLeave = (e: MouseEvent) => {
      if (!mql.matches) return;
      const li = (e.currentTarget as HTMLElement);
      li.classList.remove(openClass);
      const label = li.querySelector<HTMLElement>(labelSelector);
      if (label) label.setAttribute("aria-expanded", "false");
      const sub = li.querySelector<HTMLElement>(submenuSelector);
      if (sub) sub.style.display = "none";
    };

    // click (for desktop touchpads or explicit click-to-open)
    const onLabelClick = (e: Event) => {
      if (!mql.matches) return;
      const target = e.target as HTMLElement;
      const label = target.closest(labelSelector) as HTMLElement | null;
      if (!label) return;

      // prevent navigation if it's an anchor
      if (label.tagName === "A") e.preventDefault();

      const li = label.closest(itemSelector) as HTMLElement | null;
      if (!li) return;

      const isOpen = li.classList.contains(openClass);
      if (isOpen) {
        li.classList.remove(openClass);
        label.setAttribute("aria-expanded", "false");
        const sub = li.querySelector<HTMLElement>(submenuSelector);
        if (sub) sub.style.display = "none";
      } else {
        openItem(li);
      }
    };

    // focus (keyboard users)
    const onFocusIn = (e: FocusEvent) => {
      if (!mql.matches) return;
      const target = e.target as HTMLElement;
      const li = target.closest(itemSelector) as HTMLElement | null;
      if (!li) return;
      openItem(li);
    };
    const onFocusOut = (e: FocusEvent) => {
      if (!mql.matches) return;
      // give time for focus to move inside submenu
      setTimeout(() => {
        const active = document.activeElement as HTMLElement | null;
        const stillInside = active?.closest(itemSelector);
        if (!stillInside) closeAll();
      }, 0);
    };

    // esc to close
    const onKeyDown = (e: KeyboardEvent) => {
      if (!mql.matches || !closeOnEsc) return;
      if (e.key === "Escape") closeAll();
    };

    // attach listeners
    items.forEach((li) => {
      li.addEventListener("mouseenter", onMouseEnter);
      li.addEventListener("mouseleave", onMouseLeave);
      li.addEventListener("focusin", onFocusIn);
      li.addEventListener("focusout", onFocusOut);
      const label = li.querySelector(labelSelector);
      if (label) {
        label.setAttribute("aria-haspopup", "true");
        label.setAttribute("aria-expanded", "false");
        label.addEventListener("click", onLabelClick);
      }
      // ensure hidden by default
      const sub = li.querySelector<HTMLElement>(submenuSelector);
      if (sub) sub.style.display = "none";
    });

    window.addEventListener("keydown", onKeyDown);

    // on resize: if moving from mobile->desktop or vice-versa, close all
    const onResize = () => closeAll();
    window.addEventListener("resize", onResize);

    return () => {
      items.forEach((li) => {
        li.removeEventListener("mouseenter", onMouseEnter);
        li.removeEventListener("mouseleave", onMouseLeave);
        li.removeEventListener("focusin", onFocusIn);
        li.removeEventListener("focusout", onFocusOut);
        const label = li.querySelector(labelSelector);
        if (label) label.removeEventListener("click", onLabelClick);
      });
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);
    };
  }, [
    itemSelector,
    labelSelector,
    submenuSelector,
    openClass,
    desktopMinWidth,
    closeOnEsc,
  ]);
}
